// @flow

import React from 'react';
import useSheet from 'app/jss';
import t from 'app/utils/translate';
import ConfirmDialog from 'app/components/friends/common/ConfirmDialog';
import GroupContainer from 'app/components/friends/GroupContainer';
import Icon from 'app/components/commons/Icon';
import GroupContainerHeader from 'app/components/friends/GroupContainerHeader';
import BouncyLoader from 'app/components/commons/BouncyLoader';

type Props = {
  classes: Object,
  onCancelRemove: () => void,
  onConfirmRemove: () => void,
  group?: FriendGroup,
  removing?: boolean,
  removeFailed?: boolean,
  removeSuccess?: boolean,
};

type State = {
  showRemoveFailed: boolean,
  group: FriendGroup,
};


class GroupDeleteConfirm extends React.Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      showRemoveFailed: false,
      group: props.group,
    };
  }

  static defaultProps = {
    removing: false,
    removeFailed: false,
    removeSuccess: false,
    group: undefined,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // keep track of our group in state so we can
    if (this.props.group !== nextProps.group && !nextProps.group) {
      this.setState({ group: nextProps.group });
    }

    if (!this.props.removeFailed && nextProps.removeFailed) {
      this.setState({ showRemoveFailed: true });
      this.failTimeout = window.setTimeout(() => this.setState({ showRemoveFailed: false }, 4000));
    }
  }

  componentWillUnmount() {
    if (this.failTimeout) {
      window.clearTimeout(this.failTimeout);
    }
  }

  render() {
    const {
      classes,
      onCancelRemove,
      onConfirmRemove,
      removing,
    } = this.props;
    const {
      showRemoveFailed,
      group,
    } = this.state;

    return (
      <div className={classes.deleteContainer}>
        <GroupContainer invert className={classes.container}>
          <GroupContainerHeader invert>
            <div className={classes.groupTitle}>
              <Icon className={classes.icon} type="icon-bin" />
              <span>{group.name}</span>
              <Icon className={classes.shrink} type="icon-shrink" />
            </div>
          </GroupContainerHeader>
          {removing && !showRemoveFailed &&
            <div className={classes.removal}>
              <span className={classes.largeText}>Deleting group</span>
              <div className={classes.loaderContainer}>
                <BouncyLoader />
              </div>
            </div>
          }
          {showRemoveFailed &&
            <div className={classes.removal}>
              <span className={classes.largeText}>{t('Failed to delete, sorry!')} :(</span>
            </div>
          }
          {!removing && !showRemoveFailed &&
            <ConfirmDialog
              title={t('Delete group')}
              onCancel={onCancelRemove}
              onConfirm={onConfirmRemove}
              transparentConfirm
            >
              {t('Do you want to delete this group?')}
            </ConfirmDialog>}
        </GroupContainer>
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    icon: {
      fontSize: 14,
    },
    container: {
      height: '100%',
    },
    loaderContainer: {
      width: 50,
    },
    removal: {
      display: 'flex',
      flexDirection: 'column',
      color: theme.color.basis6,
      textAlign: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
      flexGrow: 1,
      '& > *': {
        marginBottom: 10,
      },
    },
    deleteContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: theme.zIndex.fullscreen,
      overflow: 'hidden',
    },
    largeText: {
      fontSize: 24,
      fontWeight: 600,
      color: theme.color.basis6,
      textTransform: 'uppercase',
    },
    groupTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      textTransform: 'uppercase',
      fontWeight: 600,
      fontSize: 18,
      color: theme.color.basis6,
      fontSmoothing: 'antialiased',
    },
    shrink: {
      color: theme.color.basis3,
    },
  };
}

export default useSheet(applyStyles)(GroupDeleteConfirm);
